// src/pages/admin/AddTelegramPackage.jsx
import React, { useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  LinearProgress,
  FormControlLabel,
  Switch,
  Container,
} from "@mui/material";
import { createTelegramPackage } from "../../services/api";

export default function AddTelegramPackage() {
  const [noteText, setNoteText] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isHidden, setIsHidden] = useState(false);
  const [isFeatured, setIsFeatured] = useState(false);

  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const handleImageChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setPreview(URL.createObjectURL(file));
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!noteText.trim()) {
      setSnack({
        open: true,
        severity: "error",
        message: "Please write package details first",
      });
      return;
    }

    // ensure admin token exists for interceptor (if your axios interceptor uses admin_token)
    const foundToken =
      localStorage.getItem("admin_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      localStorage.getItem("adminToken") ||
      localStorage.getItem("jwt");

    if (!foundToken) {
      setSnack({
        open: true,
        severity: "error",
        message: "Missing admin token. Please login again.",
      });
      return;
    }
    if (!localStorage.getItem("admin_token")) {
      localStorage.setItem("admin_token", foundToken);
    }

    setLoading(true);
    try {
      // If you later add server-side upload, send the File; for now we just pass preview URL as imageUrl.
      const payload = {
        noteText,
        imageUrl: preview || undefined,
        isHidden,
        isFeatured,
      };

      const res = await createTelegramPackage(payload);

      if (res?.data?.ok) {
        setSnack({
          open: true,
          severity: "success",
          message: "Telegram package created successfully",
        });
        // reset
        setNoteText("");
        setImageFile(null);
        setPreview(null);
        setIsHidden(false);
        setIsFeatured(false);
      } else {
        setSnack({
          open: true,
          severity: "error",
          message: res?.data?.error || res?.data?.message || "Failed to create package",
        });
      }
    } catch (err) {
      console.error("createTelegramPackage error:", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Server error";
      setSnack({ open: true, severity: "error", message: msg });
    } finally {
      setLoading(false);
    }
  }, [noteText, preview, isHidden, isFeatured]);

  return (
    <Box sx={{ pt: { xs: 10, sm: 12 }, pb: 6, minHeight: "70vh" }}>
      <Container maxWidth="md">
        <Paper
          sx={{
            p: { xs: 2, sm: 4 },
            boxShadow: "0 8px 30px rgba(2,6,23,0.5)",
            maxWidth: "100%",
          }}
        >
          <Typography variant="h6" gutterBottom>
            Add Telegram Package (Notepad Style)
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
            Paste/write your package details below (e.g., “Name: …”, “Duration: …”, “Monthly Profit: …”). Title will be auto-derived from “Name:”/“Title:” or first line.
          </Typography>

          <TextField
            label="Package Details"
            multiline
            minRows={10}
            fullWidth
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder={`Example:
Name: 6 Month Flexible Investment Plan
Duration: 6 Months
Investment Range: Minimum 1,000 USDT, Maximum 10,000 USDT
Monthly Profit: 3%–5% based on tiers
Profit Payment: Withdraw OR Reinvest monthly
Deposit Lock: 6 months
Special Bonus: Extra 1% to the first 100 customers`}
          />

          <Box sx={{ mt: 3, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <Button variant="contained" component="label">
              {imageFile ? "Change Image" : "Upload Package Image"}
              <input type="file" accept="image/*" hidden onChange={handleImageChange} />
            </Button>

            <FormControlLabel
              control={
                <Switch
                  checked={isHidden}
                  onChange={(e) => setIsHidden(e.target.checked)}
                />
              }
              label="Hidden"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={isFeatured}
                  onChange={(e) => setIsFeatured(e.target.checked)}
                />
              }
              label="Featured"
            />
          </Box>

          {preview && (
            <Box sx={{ mt: 2 }}>
              <img
                src={preview}
                alt="Preview"
                style={{
                  maxWidth: "100%",
                  borderRadius: 8,
                  boxShadow: "0 0 10px rgba(0,0,0,0.3)",
                }}
              />
            </Box>
          )}

          {loading && <LinearProgress sx={{ mt: 3 }} />}

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setNoteText("");
                setImageFile(null);
                setPreview(null);
                setIsHidden(false);
                setIsFeatured(false);
              }}
            >
              Reset
            </Button>
            <Button variant="contained" color="primary" onClick={handleSubmit} disabled={loading}>
              {loading ? "Saving..." : "Create Telegram Package"}
            </Button>
          </Box>
        </Paper>

        <Snackbar
          open={snack.open}
          autoHideDuration={4000}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          <Alert
            onClose={() => setSnack((s) => ({ ...s, open: false }))}
            severity={snack.severity}
            sx={{ width: "100%" }}
          >
            {snack.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}
